import React, { useState, useEffect, useCallback } from 'react';
import { Card, ListGroup, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { Clock, Person, Shield, CreditCard, Database, ExclamationTriangle, CurrencyDollar, Gear, Pencil, Key, ArrowRightShort, ArrowClockwise } from 'react-bootstrap-icons';
import api from '../services/api';

const RecentActivity = ({ limit = 10, showTitle = true }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRecentActivities = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await api.get(`/api/my/recent-activities?limit=${limit}`);
      
      if (response.data.success) {
        setActivities(response.data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError('Failed to fetch recent activities');
      }
    } catch (err) {
      console.error('Error fetching recent activities:', err);
      setError('Error loading recent activities');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecentActivities();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRecentActivities(true);
    }, 30000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [fetchRecentActivities]);

  const handleManualRefresh = () => {
    fetchRecentActivities(true);
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      'login': <Shield className="text-success" />,
      'logout': <ArrowRightShort className="text-warning" />,
      'payment_request': <CreditCard className="text-primary" />,
      'admin_action': <Gear className="text-info" />,
      'user_registration': <Person className="text-success" />,
      'system_backup': <Database className="text-secondary" />,
      'security_alert': <ExclamationTriangle className="text-danger" />,
      'wallet_transfer': <CurrencyDollar className="text-success" />,
      'profile_update': <Pencil className="text-warning" />,
      'password_change': <Key className="text-info" />
    };
    return icons[activityType] || <Clock className="text-muted" />;
  };

  const getRoleBadge = (role) => {
    const variants = {
      'admin': 'danger',
      'distributor': 'warning',
      'user': 'primary'
    };
    return (
      <Badge bg={variants[role] || 'secondary'} className="ms-2">
        {role}
      </Badge>
    );
  };

  const formatTimeAgo = (timeAgo) => {
    // Convert time ago to more readable format
    if (timeAgo.includes('minute')) {
      const minutes = timeAgo.match(/\d+/)?.[0] || '0';
      return `${minutes} min ago`;
    } else if (timeAgo.includes('hour')) {
      const hours = timeAgo.match(/\d+/)?.[0] || '0';
      return `${hours} hour${hours !== '1' ? 's' : ''} ago`;
    } else if (timeAgo.includes('day')) {
      const days = timeAgo.match(/\d+/)?.[0] || '0';
      return `${days} day${days !== '1' ? 's' : ''} ago`;
    }
    return timeAgo;
  };

  const formatLastUpdated = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) !== 1 ? 's' : ''} ago`;
    return date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Card className="h-100">
        {showTitle && (
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <Clock className="me-2" />
              Recent Activity
            </h5>
          </Card.Header>
        )}
        <Card.Body className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-100">
        {showTitle && (
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <Clock className="me-2" />
              Recent Activity
            </h5>
          </Card.Header>
        )}
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
          <div className="text-center">
            <Button variant="outline-primary" size="sm" onClick={handleManualRefresh}>
              <ArrowClockwise className="me-1" />
              Retry
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      {showTitle && (
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <Clock className="me-2" />
            Recent Activity
          </h5>
          <div className="d-flex align-items-center">
            {refreshing && (
              <Spinner animation="border" size="sm" variant="light" className="me-2" />
            )}
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-1"
            >
              <ArrowClockwise className={refreshing ? 'spinning' : ''} />
            </Button>
          </div>
        </Card.Header>
      )}
      <Card.Body className="p-0">
        {activities.length === 0 ? (
          <div className="text-center py-4">
            <Clock size={48} className="text-muted mb-3" />
            <p className="text-muted">No recent activity</p>
          </div>
        ) : (
          <ListGroup variant="flush">
            {activities.map((activity, index) => (
              <ListGroup.Item 
                key={activity.id} 
                className={`d-flex align-items-start py-3 ${
                  index === 0 ? 'border-top-0' : ''
                }`}
                style={{
                  borderLeft: 'none',
                  borderRight: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="me-3 mt-1">
                  {getActivityIcon(activity.activity_type)}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold text-dark">
                        {activity.description}
                      </p>
                      <div className="d-flex align-items-center">
                        <small className="text-muted">
                          {activity.user_name}
                        </small>
                        {getRoleBadge(activity.user_role)}
                        <small className="text-muted ms-2">
                          {activity.user_email}
                        </small>
                      </div>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">
                        {formatTimeAgo(activity.time_ago)}
                      </small>
                      <small className="text-muted">
                        {activity.formatted_date_time}
                      </small>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
      {activities.length > 0 && (
        <Card.Footer className="bg-light text-center">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Showing {activities.length} recent activities
            </small>
            {lastUpdated && (
              <small className="text-muted">
                Updated {formatLastUpdated(lastUpdated)}
              </small>
            )}
          </div>
        </Card.Footer>
      )}
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default RecentActivity;
